import { getProductById } from '../api/preorder';
import { addToCart } from '../modules/cart/cart.legacy';
import { getTemplate } from '../template';
import { formatCurrency, getSessionStorage, setSessionStorage, setWindowLocation } from '../utils';
import { isKeyboardActivated } from '../utils/keyboardActivated.js';
import { focusFirstElement, trapFocus } from '../utils/wcag-modal';

/**
 * @type {import('./index.d').getVariantIdByUrl}
 */
export const getVariantIdByUrl = () => {
  const url = new URL(window.location.href);
  return url.searchParams.get('variant') || '';
};

/**
 * @type {import('./index.d').getFirstVariant}
 */
const getFirstVariant = async (context) => {
  const product = await getProductById(context, window.boostSDData.product.id);
  if (!product) return null;
  window.boostSdPreOrderData = product;
  const variants = product.variants;
  const variantId = getVariantIdByUrl();
  setSessionStorage('preVariantIdSelected', variantId);
  return variantId
    ? variants.find((variant) => String(variant?.id) === String(variantId))
    : variants.find((variant) => variant?.available);
};

/**
 * @type {import('./index.d').initEventChangeVariantUrlProductPage}
 */
const initEventChangeVariantUrlProductPage = (context) => {
  const checkAndUpdateVariant = () => {
    let attempts = 0;
    const maxAttempts = 10;
    const delay = 250;
    const preVariantId = getSessionStorage('preVariantIdSelected');

    const check = () => {
      const [, setVariant] = context.useContextState('variant-product-page', {});
      const currentVariantId = getVariantIdByUrl();

      if (currentVariantId && currentVariantId !== preVariantId) {
        setSessionStorage('preVariantIdSelected', currentVariantId);
        const variantSelected = window.boostSdPreOrderData?.variants?.find(
          (variant) => String(variant?.id) === String(currentVariantId)
        );
        setVariant(variantSelected);
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(check, delay);
      } else {
        console.log('Variant ID not changed after 5 retries.');
      }
    };

    check();
  };

  // Listen for variant changes across different theme implementations
  // Supports Dawn, Refresh, Venue, Horizon, Motion, Streamline, etc.
  // Safari requires 'change' events for dropdowns instead of just 'click'
  const VARIANT_UI_SELECTOR = `
    select[name^="options["],
    input[name^="options["],
    [data-variant-input],
    input[data-option-value-id],
    input[type="radio"][data-option-position],
    input[type="radio"].js-option,
    input.disclosure--input[data-option-name],
    .product-form__single-selector
  `;

  const handleChange = (event) => {
    if (event.target.matches(VARIANT_UI_SELECTOR)) {
      checkAndUpdateVariant();
    }
  };
  const handleClick = (event) => {
    if (
      event.target.closest('li.custom-select__option.js-option') ||
      event.target.closest('li.disclosure--option')
    ) {
      checkAndUpdateVariant();
    }
  };
  const handlePopState = () => {
    checkAndUpdateVariant();
  };
  const handleVariantDropdown = () => {
    checkAndUpdateVariant();
  };
  document.body.addEventListener('change', handleChange);
  document.body.addEventListener('click', handleClick);
  window.addEventListener('popstate', handlePopState);

  // Custom event fallback for unsupported themes
  // Merchants can dispatch: window.dispatchEvent(new Event('boost-sd-variant-dropdown'))
  window.addEventListener('boost-sd-variant-dropdown', handleVariantDropdown);
  // Return cleanup function to remove listeners
  return () => {
    document.body.removeEventListener('change', handleChange);
    document.body.removeEventListener('click', handleClick);
    window.removeEventListener('popstate', handlePopState);
    window.removeEventListener('boost-sd-variant-dropdown', handleVariantDropdown);
  };
};

/**
 * @type {import('./index.d').showPreorderBlock}
 */
export const showPreorderBlock = (preorderVariant) => {
  document?.querySelectorAll('.boost-sd__preorder')?.forEach((block) => {
    if (block) block.style.display = 'none';
  });
  const matchingPreorderBlock = document.querySelector(
    `.boost-sd__preorder[data-variant-id="${preorderVariant}"]`
  );
  if (matchingPreorderBlock) {
    matchingPreorderBlock.style.display = 'block';
  }
};

/**
 * @type {import('./index.d').cbAfterRenderPreOrder}
 */
const cbAfterRenderPreOrder = (context) => {
  const form = document.querySelector('.boost-sd__product-form');
  if (!form) return;

  const inputNumber = form.querySelector('.boost-sd__input-number-input');
  const decreaseButton = form.querySelector('.boost-sd__input-number-btn-wrapper:first-child');
  const increaseButton = form.querySelector('.boost-sd__input-number-btn-wrapper:last-child');
  const errorQuantity = form.querySelector('.boost-sd__product-form-quantity-error');
  const purchaseButton = form.querySelector('.boost-sd__button--purchase');
  const buyItNowButton = form.querySelector('.boost-sd__btn-buy-now');
  const preorderButton = form.querySelector('.boost-sd__button--preorder');
  const preorderButtonText = form.querySelector(
    '.boost-sd__button--preorder .boost-sd__button-text'
  );
  const purchaseButtonText = form.querySelector(
    '.boost-sd__button--purchase .boost-sd__button-text'
  );
  const noteBlock = form.querySelector('.boost-sd__preorder-note');
  const noteText = form.querySelector('.boost-sd__preorder-note-text');
  const btnBlock = form.querySelector('.boost-sd__purchase-options');
  const orderBlock = form.querySelector('.boost-sd__preorder-options');

  if (isKeyboardActivated()) {
    focusFirstElement(form);
    form.addEventListener('keydown', (e) => trapFocus(e, form));
  }

  decreaseButton?.addEventListener('click', (e) => {
    e.preventDefault();
    if (inputNumber) {
      inputNumber.value = Math.max(1, Number(inputNumber.value) - 1);
      if (errorQuantity) errorQuantity.style.display = 'none';
    }
  });

  increaseButton?.addEventListener('click', (e) => {
    e.preventDefault();
    if (inputNumber) {
      inputNumber.value = Math.max(1, Number(inputNumber.value) + 1);
      if (errorQuantity) errorQuantity.style.display = 'none';
    }
  });

  inputNumber?.addEventListener('keypress', (e) => {
    const charCode = e.which || e.keyCode;
    if (charCode !== 46 && charCode > 31 && (charCode < 48 || charCode > 57)) e.preventDefault();
    if (errorQuantity) errorQuantity.style.display = 'none';
  });

  purchaseButton?.addEventListener('click', async () => {
    if (inputNumber && Number(inputNumber.value) <= 0) {
      if (errorQuantity) errorQuantity.style.display = 'block';
      return;
    }

    const [getVariant] = context.useContextState('variant-product-page', {});
    const selectedVariant = getVariant();
    context.handler(addToCart, 'addToCart')(
      context,
      {
        productId: selectedVariant.id,
        quantity: Number(inputNumber.value),
      },
      purchaseButton,
      false,
      () => {
        setTimeout(() => {
          const cartModal = document.querySelector('#boost-sd__modal-cart');
          if (cartModal) focusFirstElement(cartModal);
        }, 300);
      }
    );
  });

  preorderButton?.addEventListener('click', async () => {
    const [getVariant] = context.useContextState('variant-product-page', {});
    const selectedVariant = getVariant();

    const { enableCart } = context?.templateSettings?.themeSettings?.cart || {};
    const cartItem = handlePreorderAddToCart(selectedVariant);
    cartItem.quantity = Number(inputNumber.value);

    context.handler(addToCart, 'addToCart')(context, cartItem, preorderButton, enableCart, () => {
      setTimeout(() => {
        const cartModal = document.querySelector('#boost-sd__modal-cart');
        if (cartModal) focusFirstElement(cartModal);
      }, 300);
    });
  });

  buyItNowButton?.addEventListener('click', () => {
    if (inputNumber && Number(inputNumber.value) === 0) return;
    const { value } = inputNumber;
    if (selectedVariant) {
      setWindowLocation(`/cart/${selectedVariant.id}:${value}`);
    }
  });

  const [getVariant] = context.useContextState('variant-product-page', {});
  const selectedVariant = getVariant();
  if (!purchaseButton || !selectedVariant) return;

  if (Object.keys(selectedVariant).length === 0) {
    if (preorderButton) preorderButton.style.display = 'none';
    if (noteBlock) noteBlock.style.display = 'none';
  }

  const product = window.boostSDData?.product || {};
  if (!product || !Array.isArray(product.variants)) {
    return null;
  }
  const variants = product.variants;
  const isFirstVariantOutStock = variants.length > 0 && !variants[0].available;
  const isSoldOutVariantSelected =
    (selectedVariant && Object.keys(selectedVariant).length === 0 && isFirstVariantOutStock) ||
    (selectedVariant && Object.keys(selectedVariant).length > 0 && !selectedVariant.available);

  const isPreOrder = selectedVariant && selectedVariant.preorder;

  if (isPreOrder) {
    if (btnBlock) btnBlock.style.display = 'none';
    if (orderBlock) {
      orderBlock.style.display = 'block';
    }
    showPreorderBlock(selectedVariant.id);

    const buttonPreOrderSetting = selectedVariant.preorder?.settings?.buttonText;
    const notePreOrderSetting = selectedVariant.preorder?.settings?.showDeliveryText;
    const notePreOrderText = selectedVariant.preorder?.settings?.deliveryText;
    if (preorderButton && buttonPreOrderSetting) {
      if (preorderButtonText) {
        preorderButtonText.textContent = buttonPreOrderSetting;
      }
    } else {
      if (preorderButtonText) {
        preorderButtonText.textContent = 'Pre-Order';
      }
    }
    if (noteBlock && notePreOrderSetting === true) {
      noteBlock.style.display = 'block';
      noteText.textContent = notePreOrderText;
    }
  } else {
    if (orderBlock) {
      orderBlock.style.display = 'none';
    }
    if (btnBlock) btnBlock.style.display = 'flex';

    if (isSoldOutVariantSelected) {
      if (purchaseButton) {
        purchaseButton.setAttribute('disabled', true);
        if (purchaseButtonText) {
          purchaseButtonText.textContent = 'Sold out';
        }
      }

      if (buyItNowButton) {
        buyItNowButton.setAttribute('disabled', true);
      }
    } else {
      if (purchaseButton) {
        purchaseButton.removeAttribute('disabled', true);
        if (purchaseButtonText) {
          purchaseButtonText.textContent =
            context.app?.translation?.productItem?.atcAvailableLabel || 'Add to cart';
        }
      }

      if (buyItNowButton) {
        buyItNowButton.removeAttribute('disabled', true);
      }
    }
  }
};

/**
 * @type {import('./index.d').renderPreOrder}
 */
export const initPreOrderEvents = async (context) => {
  initEventChangeVariantUrlProductPage(context);
  context.render(async () => {
    const preOrderDom = document.querySelector('.boost-sd__preOrder-container');
    if (!preOrderDom) {
      return;
    }
    await renderPreOrder(context, preOrderDom);
  }, ['variant-product-page']);
};

/**
 * @type {import('./index.d').renderPreOrder}
 */
export async function renderPreOrder(context, dom) {
  const product = window.boostSdPreOrderData || {};
  const { template = {} } = context.app;
  const { preOrderTemp = '' } = template;

  const preOrderTemplate =
    preOrderTemp !== '' ? preOrderTemp : getTemplate(context).preOrderTemplate;

  const html = await context.templateRender(preOrderTemplate, {
    ...product,
    request: {
      money_format: context.app.shop.money_format,
      money_format_with_currency: context.app.shop.money_format_with_currency,
      currency_rate: window.Shopify?.currency?.rate,
    },
  });

  if (dom) {
    dom.innerHTML = html;
    cbAfterRenderPreOrder(context);
  }
}

/**
 * @type {import('./index.d').renderPreOrderBlocksForProductPage}
 */
export const renderPreOrderBlocksForProductPage = async (context) => {
  const preOrderDom = document.querySelector('.boost-sd__preOrder-container');
  if (preOrderDom?.length === 0) return;
  // Initiate pre-order events and render the actual content
  await initPreOrderEvents(context);
  const firstVariant = await getFirstVariant(context);
  const [, setVariant] = context.useContextState('variant-product-page', {});
  setVariant(firstVariant);
};

/**
 * Function to get the original selling price
 * @type {import('./index.d').getOriginSellingPrice}
 */
export const getOriginSellingPrice = (item) => {
  return (item?.selling_plan_allocation?.compare_at_price || 0) / 100;
};

/**
 * Function to get the final selling price
 * @type {import('./index.d').getFinalSellingPrice}
 */
export const getFinalSellingPrice = (item) => {
  return (item?.selling_plan_allocation?.price || 0) / 100;
};

/**
 * @type {import('./index.d').updateSellingPlanAllocation}
 */
export const updateSellingPlanAllocation = (item, context) => {
  const { showCurrencyCodes } =
    context?.templateSettings?.themeSettings?.productItems?.productInfo?.elements?.price || {};
  if (item?.selling_plan_allocation) {
    item.selling_plan_allocation.price = formatCurrency({
      context,
      value: getFinalSellingPrice(item),
      showCurrencyCodes,
    });
    item.selling_plan_allocation.compare_at_price = formatCurrency({
      context,
      value: getOriginSellingPrice(item),
      showCurrencyCodes,
    });
  }
  return item;
};

/**
 * Function to check if the button is pre-order and set the appropriate text
 * @type {import('./index.d').setPreOrderButtonText}
 */
export const setPreOrderButtonText = (element, context) => {
  const [getVariant] = context.useContextState('variant-product-page', {});
  const selectedVariant = getVariant();

  if (
    !element.classList.contains('boost-sd__button--icon') &&
    (element?.classList.contains('boost-sd__button-text--preorder') ||
      element?.classList.contains('boost-sd__button--preorder'))
  ) {
    element.textContent = selectedVariant.preorder?.settings?.buttonText || 'Pre-order';
    return true;
  }
  return false;
};

/**
 * @type {import('./index.d').getSellingPlanId}
 */
export function getSellingPlanId(selectedVariant) {
  return selectedVariant?.preorder?.sellingPlan?.id || null;
}

/**
 * @type {import('./index.d').handlePreorderAddToCart}
 */
export function handlePreorderAddToCart(selectedVariant) {
  const sellingPlanId = getSellingPlanId(selectedVariant);

  return {
    productId: selectedVariant.id,
    quantity: 1,
    ...(sellingPlanId && { selling_plan: sellingPlanId }), // Include only if available
  };
}

/**
 * @type {import('./index.d').isPreOrderVariant}
 */
export function isPreOrderVariant(selectedVariant) {
  return !!selectedVariant?.preorder;
}

/**
 * @type {import('./index.d').getPreOrderButtonText}
 */
export function getPreOrderButtonText(selectedVariant) {
  return selectedVariant?.preorder?.settings?.buttonText || 'Pre-Order';
}

/**
 * @type {import('./index.d').getPreOrderNoteText}
 */
export function getPreOrderNoteText(selectedVariant) {
  return selectedVariant?.preorder?.settings?.showDeliveryText
    ? selectedVariant.preorder?.settings?.deliveryText || ''
    : null;
}

/**
 * @type {import('./index.d').handlePreOrderButtonState}
 */
export function handlePreOrderButtonState(addToCartButton, addToCartButtonText, selectedVariant) {
  if (!addToCartButton || !selectedVariant) return;

  if (isPreOrderVariant(selectedVariant)) {
    addToCartButton.classList.add('boost-sd__button--preorder');
    addToCartButton.removeAttribute('disabled');

    if (addToCartButtonText) {
      addToCartButtonText.textContent = getPreOrderButtonText(selectedVariant);
    }
  } else {
    addToCartButton.classList.remove('boost-sd__button--preorder');
  }
}

/**
 * @type {import('./index.d').handlePreOrderUI}
 */
export function handlePreOrderUI(selectedVariant, elements) {
  const { btnBlock, orderBlock, preorderButtonText, noteBlock, noteText, showPreorderBlock } =
    elements;
  const isPreOrder = isPreOrderVariant(selectedVariant);

  if (isPreOrder) {
    btnBlock.style.display = 'none';
    orderBlock.style.display = 'block';
    showPreorderBlock(selectedVariant.id);

    if (preorderButtonText) {
      preorderButtonText.textContent = getPreOrderButtonText(selectedVariant);
    }

    const notePreOrderText = getPreOrderNoteText(selectedVariant);
    if (noteBlock && notePreOrderText) {
      noteBlock.style.display = 'block';
      if (noteText) noteText.textContent = notePreOrderText;
    }
  } else {
    orderBlock.style.display = 'none';
    btnBlock.style.display = 'flex';
  }
}
